'use client';

import {
  formatClassNames,
  getDataAttributes,
} from '@wix/editor-elements-common-utils';
import classNames from 'clsx';
import styles from './TextOnPath.scss';
import * as React from 'react';
import semanticClassNames from '../TextOnPath.semanticClassNames';
import type { TextOnPathProps } from '../TextOnPath.types';
import { useSvgMeasurement } from './useSvgMeasurement';
import { TextOnPathA11yWrapper } from './components/TextOnPathA11yWrapper';
import { TextOnPathSvg } from './components/TextOnPathSvg';
import { ViewerTestIds } from '../constants';

function TextOnPathRoot({
  translations,
  viewBox,
  pathLength,
  customClassNames = [],
  animate,
  ...props
}: TextOnPathProps) {
  const hasPathLengthAndViewBoxOnProps = !!pathLength && !!viewBox;
  const { measureRefCallback, measurement, pathMeasurement } =
    useSvgMeasurement(hasPathLengthAndViewBoxOnProps);
  const hasPathAndViewBox =
    hasPathLengthAndViewBoxOnProps ||
    (!!pathMeasurement?.pathLength && !!pathMeasurement?.viewBox);
  const isAnimated = animate !== false && props.speed > 0 && !!measurement;
  const hasLink = !!props.link;
  const pauseOnHover = props.pauseOnHover || hasLink;

  return (
    <div
      id={props.id}
      data-testid={ViewerTestIds.root}
      className={classNames(
        styles.textOnPathRoot,
        formatClassNames(semanticClassNames.root),
        customClassNames,
      )}
      {...getDataAttributes(props)}
      data-mode={props.fillMode}
      data-animated={animate !== false && props.speed > 0 ? '' : undefined}
    >
      {/* Measurement SVG */}
      <svg
        ref={measureRefCallback}
        aria-hidden="true"
        data-nosnippet="true"
        className={classNames(
          styles.textOnPathRootSvg,
          styles.textOnPathSvgMeasure,
        )}
        preserveAspectRatio="xMidYMid meet"
        viewBox={viewBox}
      >
        <path d={props.path} data-measure-path />
        <text data-measure-height x="0" y="0" dominantBaseline="hanging">
          <tspan>ÁjgqÎ|QWERTYqwerty</tspan>
        </text>

        <text x="0" y="0" data-measure-width>
          {props.text}
          {props.fillMode === 'repeat' ? <>&nbsp;</> : null}
        </text>
      </svg>
      {hasPathAndViewBox ? (
        <TextOnPathA11yWrapper
          translations={translations}
          pauseOnHover={pauseOnHover}
          isAnimated={isAnimated}
        >
          <TextOnPathSvg
            {...props}
            speed={isAnimated ? props.speed : 0}
            viewBox={viewBox || pathMeasurement?.viewBox}
            pathLength={pathLength || pathMeasurement?.pathLength}
            measurement={measurement}
            pauseOnHover={pauseOnHover}
            isAnimated={isAnimated}
          />
        </TextOnPathA11yWrapper>
      ) : null}
    </div>
  );
}

export default TextOnPathRoot;
